// "How we count": how Panther computes every figure the terminal shows, per
// metric and per venue, so readers can trust and reproduce the numbers. The
// per-venue parts are rendered from src/methodology.ts, not written here.

import { useEffect, type ReactNode } from "react";

import { API_BASE } from "../api";
import {
  COVERAGE_SHARE,
  METHODOLOGY_REVIEWED_ON,
  PRICE_BASES,
  PRICE_MAX_AGE_HOURS,
  REFRESH_MINUTES,
  SOURCES,
  VENUE_METHODS,
  type PriceBasis,
} from "../methodology";
import styles from "./Methodology.module.css";

const SECTIONS = [
  { id: "overview", title: "Overview" },
  { id: "refresh", title: "Refresh and freshness" },
  { id: "metrics", title: "Metric definitions" },
  { id: "venues", title: "Venues at a glance" },
  { id: "venue-detail", title: "Venue by venue" },
  { id: "classes", title: "Asset classes" },
  { id: "represented", title: "Distributed and represented" },
  { id: "yields", title: "Yields and SOFR" },
  { id: "flows", title: "Net flows" },
  { id: "sources", title: "Sources" },
  { id: "limits", title: "Known limitations" },
  { id: "api", title: "The API" },
] as const;

type SectionId = (typeof SECTIONS)[number]["id"];

const COVERAGE = `${COVERAGE_SHARE * 100}%`;

function Section({ id, children }: { id: SectionId; children: ReactNode }) {
  const title = SECTIONS.find((section) => section.id === id)!.title;
  return (
    <section aria-labelledby={`${id}-heading`} className={styles.section} id={id}>
      <h2 id={`${id}-heading`}>{title}</h2>
      {children}
    </section>
  );
}

function Metric({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className={styles.metric}>
      <dt>{term}</dt>
      <dd>{children}</dd>
    </div>
  );
}

const basisLabels = (bases: PriceBasis[]) => bases.map((b) => PRICE_BASES[b].label).join(" → ");

export function Methodology() {
  useEffect(() => {
    const previous = document.title;
    document.title = "How we count — Panther Terminal";
    return () => {
      document.title = previous;
    };
  }, []);

  const apiV1 = `${API_BASE}/v1`;

  return (
    <div className="terminal">
      <p className="command-bar">
        <span aria-hidden className="cursor" />
        <span>
          METHODOLOGY <em>HOW WE COUNT</em> &lt;GO&gt;
        </span>
        <span>PANTHER/TERMINAL</span>
      </p>
      <main>
        <header className="screen-head">
          <a className="head-link" href="/tokenization">
            ← Terminal
          </a>
          <a className="head-link" href={apiV1} rel="noreferrer" target="_blank">
            API
          </a>
        </header>

        <div className={styles.page}>
          <div className={styles.intro}>
            <p className={styles.eyebrow}>Panther Terminal / methodology</p>
            <h1>How we count</h1>
            <p className={styles.lede}>
              How Panther finds tokenized stocks and measures their price, supply, market cap, trading volume and
              mint + burn, venue by venue, so every number on the terminal can be checked and reproduced.
            </p>
            <p className={styles.reviewed}>
              Checked against Panther&apos;s collector and API code on{" "}
              <time dateTime={METHODOLOGY_REVIEWED_ON}>{METHODOLOGY_REVIEWED_ON}</time>.
            </p>
          </div>

          <div className={styles.layout}>
            <nav aria-label="On this page" className={styles.toc}>
              <p className={styles.tocTitle}>On this page</p>
              <ol>
                {SECTIONS.map((section) => (
                  <li key={section.id}>
                    <a href={`#${section.id}`}>{section.title}</a>
                  </li>
                ))}
              </ol>
            </nav>

            <div className={styles.content}>
              <Section id="overview">
                <p>
                  Panther tracks {VENUE_METHODS.length} venues: two chains whose stock tokens come from one issuer
                  (Robinhood on Robinhood Chain, Coinbase on Base) and issuing platforms that deploy on one or more chains.
                  For each token it records a price, an on-chain supply, a market cap and, where they can be measured,
                  24-hour flows.
                </p>
                <ul className={styles.points}>
                  <li>
                    <strong>Robinhood</strong> is collected by Panther&apos;s Robinhood job: Robinhood&apos;s public token
                    catalog and quotes, supplies read on Robinhood Chain, and Panther&apos;s own scan of every transfer.
                  </li>
                  <li>
                    <strong>Every other venue</strong> is computed by Panther&apos;s venue engine: on-chain supplies on each
                    chain, prices from CoinGecko, issuer APIs, Chainlink or the listed share, and 24h mint + burn from
                    on-chain scans.
                  </li>
                  <li>
                    <strong>Unknown is unknown.</strong> A figure that could not be measured is shown as
                    &ldquo;unavailable&rdquo; (or &ldquo;—&rdquo; on a tile) and left out of totals. It is never shown as 0.
                  </li>
                  <li>
                    <strong>Same data as everyone.</strong> The website reads the same public API anyone can call. There is
                    no private feed behind the terminal.
                  </li>
                </ul>
              </Section>

              <Section id="refresh">
                <p>
                  Everything refreshes every {REFRESH_MINUTES} minutes: the collector runs at :00 and :30 UTC. Each run is
                  identified by its slot time, shown on every board as &ldquo;as of&rdquo;.
                </p>
                <ul className={styles.points}>
                  <li>
                    The Robinhood board is usually published within a minute or two of the slot. The venue engine lets its
                    background flow scans work for several minutes before it publishes, so the other venues
                    usually appear around ten minutes after the slot.
                  </li>
                  <li>
                    If a venue&apos;s run fails, its previous board stays up with its original &ldquo;as of&rdquo; time
                    rather than being replaced by a partial one. <code>/v1/status</code> shows when each source last ran and
                    last succeeded.
                  </li>
                  <li>
                    API responses are cached at the edge for 60 seconds. The terminal re-reads the board every minute, so a
                    new run shows up without reloading.
                  </li>
                  <li>
                    A price quoted more than {PRICE_MAX_AGE_HOURS} hours ago is not used for any value. Older quotes within
                    that bound can reflect a closed market, a weekend or a once-a-day NAV.
                  </li>
                </ul>
              </Section>

              <Section id="metrics">
                <dl className={styles.metrics}>
                  <Metric term="Price">
                    USD per token. Each venue has its own source, listed under{" "}
                    <a href="#venue-detail">Venue by venue</a>. Price bases, in the order a venue prefers them:
                    <dl className={styles.bases}>
                      {(Object.keys(PRICE_BASES) as PriceBasis[]).map((basis) => (
                        <div key={basis}>
                          <dt>{PRICE_BASES[basis].label}</dt>
                          <dd>{PRICE_BASES[basis].meaning}</dd>
                        </div>
                      ))}
                    </dl>
                    A token can stand for more than one share after splits or reinvested dividends. Supply and price are
                    always put in the same unit before they are multiplied, and a per-share price is multiplied by the share
                    multiplier once, never twice. Where the multiplier is unknown, the token is not priced from its
                    underlying share.
                  </Metric>
                  <Metric term="Supply">
                    Tokens in existence, read on chain each run (for most venues, summed across every chain listed for
                    the venue). xStocks is the exception: its supply is the issuer&apos;s proof-of-reserves circulating
                    supply, counted once. Supply counts every token that exists, including tokens the issuer still holds.
                  </Metric>
                  <Metric term="Market cap">
                    Supply × price, per token. A venue&apos;s market cap is the sum of its tokens that have one; the
                    dashboard says how many tokens were priced (&ldquo;across N of M tokens&rdquo;).
                  </Metric>
                  <Metric term="Trading volume">
                    Robinhood only: the daily trading volume Robinhood reports, in USD. Panther does not compute trading
                    volume for other venues.
                  </Metric>
                  <Metric term="Mint + burn">
                    USD value of tokens created plus tokens destroyed. It measures issuance and redemption, not trading: a
                    trade between two holders mints and burns nothing. For Robinhood it is Robinhood&apos;s reported figure
                    for today. For other venues it is a rolling 24-hour scan of mint and burn transfers, valued at the
                    current price, published only once the scan covers the whole window on chains holding at least{" "}
                    {COVERAGE} of the token&apos;s supply.
                  </Metric>
                  <Metric term="24h change">
                    The token&apos;s price (and, in the API, market cap and supply) compared with its reading from the run
                    exactly 24 hours earlier: <code>now ÷ then − 1</code>. Unavailable when either reading is missing.
                  </Metric>
                  <Metric term="Share and concentration">
                    A token&apos;s share is its market cap ÷ the venue&apos;s market cap. Top-5 concentration is the five
                    largest tokens&apos; market caps ÷ the venue&apos;s. In the all-venues view, a token deployed on several
                    chains has its market cap split across chains by each chain&apos;s share of its supply, so it is
                    counted once.
                  </Metric>
                </dl>
              </Section>

              <Section id="venues">
                <p>Each venue&apos;s chains and sources, in the order the terminal lists them.</p>
                <div aria-labelledby="venues-heading" className={styles.tableScroll} role="region" tabIndex={0}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th scope="col">Venue</th>
                        <th scope="col">Chains</th>
                        <th scope="col">Price</th>
                        <th scope="col">Supply</th>
                        <th scope="col">Mint + burn</th>
                      </tr>
                    </thead>
                    <tbody>
                      {VENUE_METHODS.map((venue) => (
                        <tr key={venue.id}>
                          <th scope="row">
                            <a href={`#venue-${venue.id}`}>{venue.label}</a>
                          </th>
                          <td>{venue.chains}</td>
                          <td>{basisLabels(venue.bases)}</td>
                          <td>{venue.id === "xstocks" ? "Issuer reserves report" : "On-chain"}</td>
                          <td>{venue.flowShort}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>

              <Section id="venue-detail">
                <ul aria-label="Venue methods" className={styles.venueList}>
                  {VENUE_METHODS.map((venue) => (
                    <li key={venue.id}>
                      <article aria-labelledby={`venue-${venue.id}-title`} className={styles.venue} id={`venue-${venue.id}`}>
                        <div className={styles.venueIdentity}>
                          <h3 id={`venue-${venue.id}-title`}>{venue.label}</h3>
                          <p>{venue.chains}</p>
                        </div>
                        <dl className={styles.venueFacts}>
                          <div><dt>Tokens</dt><dd>{venue.tokens}</dd></div>
                          <div><dt>Price</dt><dd>{venue.price}</dd></div>
                          <div><dt>Supply</dt><dd>{venue.supply}</dd></div>
                          <div><dt>Market cap</dt><dd>{venue.cap.charAt(0).toUpperCase() + venue.cap.slice(1)}.</dd></div>
                          <div><dt>Mint + burn</dt><dd>{venue.flow}</dd></div>
                          {venue.extra?.map((item) => (
                            <div key={item.term}><dt>{item.term}</dt><dd>{item.text}</dd></div>
                          ))}
                        </dl>
                      </article>
                    </li>
                  ))}
                </ul>
              </Section>

              <Section id="classes">
                <dl className={styles.metrics}>
                  <Metric term="Total across classes">
                    The total across classes counts each product once. Some tokenized funds (USYC, USDY, BUIDL) also appear
                    in DefiLlama&apos;s stablecoin list; we match them by contract address, checked against the symbol, and
                    leave the stablecoin copy out of the headline. Each class page still shows them.
                  </Metric>
                  <Metric term="Money market">
                    Tokenized money market and cash funds that are not government funds (prime money market funds, overnight
                    swap funds such as Spiko Amundi SAFO). Government money market funds stay under Treasuries (U.S.) or
                    Government debt. Prices are the Chainlink NAV feed or the issuer&apos;s daily NAV, converted to USD. Share
                    classes in currencies we cannot convert yet (CHF) are listed without a value.
                  </Metric>
                  <Metric term="Commodities">
                    Tokens backed by gold, silver and other commodities. Tokens worth one troy ounce are priced from the
                    Chainlink XAU/USD and XAG/USD feeds; gram and fractional tokens from the same feeds scaled by their ratio,
                    or CoinGecko where no ratio is set. Supply is read on chain, less issuer inventory wallets where the issuer
                    labels them (Tether Gold&apos;s treasury). ETF trackers that also trade on a stock venue are counted under
                    Stocks only (listed here with their Stocks price and value); tracker certificates sold only by their
                    issuer are counted in their asset class. Kinesis Gold and Silver and Comtech
                    Gold are listed without a value until their chains can be read.
                  </Metric>
                  <Metric term="Private equity">
                    Tokenized venture fund interests and pre-IPO products. Each row says what the holder owns. A fund interest
                    is equity in a fund. Pre-IPO notes (unsecured debt of the issuer that pays on an IPO or sale), SPV interests
                    and loan participations are <strong>not company shares</strong>, and some companies dispute such transfers.
                    Values use a published NAV (for Blockchain Capital, the fund&apos;s NAV oracle) or the token&apos;s market
                    price; a product with neither is listed as &ldquo;no published NAV&rdquo;. We never invent a mark.
                  </Metric>
                  <Metric term="Real estate">
                    Tokenized real estate. Each row says what the holder owns: REIT shares, a share of the vehicle that owns one
                    property or project (for Reental, property equity or a development loan depending on the project), or a fund
                    interest. Platforms that issue one token per property (RealT, Lofty, Estate Protocol, Binaryx) are one row each,
                    valued at DefiLlama&apos;s TVL. Where an issuer values tokens at their offering price and publishes no
                    revaluation, the row says so: gains, losses and returned capital are not reflected.
                  </Metric>
                </dl>
              </Section>

              <Section id="represented">
                <dl className={styles.metrics}>
                  <Metric term="Distributed">
                    A token investors can hold and transfer: a fund share, a pool share, a note. Its supply is read on chain
                    and priced, and it counts in every figure on the site: class totals, the all-class headline, coverage
                    counts, changes and net flows.
                  </Metric>
                  <Metric term="Represented">
                    An on-chain record of assets that live off chain, kept by the originator, that no investor can hold or
                    transfer. The first is Figure HELOC: Figure writes the unpaid principal of the home equity lines it
                    originates on Provenance every day, and that attested figure is its value. A represented entry is listed
                    on its class board with a &ldquo;Represented&rdquo; badge and its value, but it is left out of every total,
                    entry count, coverage rule, change and flow figure, and out of the issuer, platform and chain
                    tables. Its value is shown apart, as &ldquo;Represented&rdquo; under the class total and the all-class
                    headline, where &ldquo;include represented&rdquo; adds it in.
                  </Metric>
                  <Metric term="Why apart">
                    The two measure different things. A distributed total is what investors hold on chain; a represented
                    figure is a book of loans the originator records on chain. Adding them would let one originator&apos;s
                    ledger outweigh every token investors actually hold. The split follows rwa.xyz&apos;s distributed and
                    represented asset values.
                  </Metric>
                  <Metric term="Overlap">
                    A distributed token can lend into loans that a represented record also counts (Hastra PRIME lends into
                    Figure&apos;s Democratized Prime, which funds lines inside the HELOC record). That only touches the total including represented; the amount is not
                    readable on chain, so it is not deducted, and the API lists it as unchecked overlap.
                  </Metric>
                  <Metric term="Missing values">
                    When a represented record is stale (over 48 hours old), its value is unavailable for that run and the
                    represented line says so; a partial sum is never shown.
                  </Metric>
                </dl>
              </Section>

              <Section id="yields">
                <dl className={styles.metrics}>
                  <Metric term="NAV yield">
                    For a fund whose token price is a NAV that grows as it earns, the yield comes from Panther&apos;s own daily
                    NAV history: (NAV now ÷ NAV 7 or 30 days earlier)<sup>365 ÷ days</sup> − 1, a compounded annual rate. The
                    30-day figure is used once it exists, else the 7-day one. Each needs that many days of our own snapshots,
                    so new entries show &ldquo;—&rdquo; at first.
                  </Metric>
                  <Metric term="Issuer yield">
                    A fund that pays its yield out (a $1 fund paying dividends as new shares, a rebasing token, a coupon
                    note) has a flat price, so the issuer&apos;s published yield is used instead: read live where the issuer
                    has an API, otherwise the figure in our registry (a fixed coupon, or a variable rate only while it is
                    under 31 days old).
                  </Metric>
                  <Metric term="Plausibility guards">
                    A yield is left out, never guessed, when the fund is not priced in USD (its USD price moves with the
                    exchange rate); when one day&apos;s NAV step is too large (over 1% for Treasuries and money market, 3% for
                    government debt, 5% for credit); when the NAV did not move over the window; or when the result is outside
                    a plausible band (−10% to 20% for Treasuries and money market, −20% to 30% for government debt, −30% to
                    50% for credit). Hovering &ldquo;—&rdquo; in the Yield column says which.
                  </Metric>
                  <Metric term="Class average">
                    For Treasuries and money market, the average yield is weighted by each entry&apos;s value, over the
                    counted entries that have a yield. It is shown only when those entries hold at least half of the
                    class&apos;s value, and the tile says how much they cover.
                  </Metric>
                  <Metric term="SOFR">
                    The Secured Overnight Financing Rate from the{" "}
                    <a href="https://www.newyorkfed.org/markets/reference-rates/sofr" rel="noreferrer" target="_blank">
                      Federal Reserve Bank of New York
                    </a>
                    , read once a day from its public API. Each business day&apos;s rate is published the next business
                    morning. SOFR is a simple overnight rate on an actual/360 basis, so it is also shown as an annual
                    compounded yield, (1 + SOFR ÷ 360)<sup>365</sup> − 1 (3.90% is 4.03% APY), and the spread is the average
                    yield minus that APY: both sides on the same basis. An issuer&apos;s 7-day SEC yield is a simple
                    annualization, and the Yield column says so on hover. The spread is not shown when the latest SOFR is
                    more than 4 business days old.
                  </Metric>
                </dl>
              </Section>

              <Section id="flows">
                <dl className={styles.metrics}>
                  <Metric term="What a net flow is">
                    Money into or out of a product: the units issued minus the units redeemed, each day valued at that
                    day&apos;s price. Positive is an inflow (green), negative an outflow (red). Shown over 1, 7 and 30
                    days, and as daily bars under each class chart and in the token panel.
                  </Metric>
                  <Metric term="What is not a flow">
                    A price move is not a flow: a fund whose share price rises with no new shares has a flow of zero. A
                    product, or a chain of one, that we start tracking is not a flow either; it is coverage growth, shown
                    apart. Only supply on chains read at both ends of a day counts. A product valued by protocol TVL has no
                    supply to measure, so its flow shows &ldquo;—&rdquo;.
                  </Metric>
                  <Metric term="Two ways to measure it">
                    Where our own transfer index covers every chain a token is on (Robinhood Chain, and tokens wholly on
                    Arbitrum, Base or Avalanche), the flow is the tokens minted minus burned, hour by hour, times the price.
                    Elsewhere it is the change in supply times the price, chain by chain. Hovering a flow says which; a
                    total that combines both says &ldquo;mixed&rdquo;.
                  </Metric>
                  <Metric term="Totals">
                    A class, venue or all-class flow is the sum over its products, published only when the products with a
                    measured flow hold at least 90% of the value; otherwise it shows &ldquo;—&rdquo;. A window shows
                    &ldquo;—&rdquo; until it has that many days of our own daily snapshots, and when any day in it is
                    unknown. Amounts under a cent are shown as $0.
                  </Metric>
                </dl>
              </Section>

              <Section id="sources">
                <ul className={styles.points}>
                  {SOURCES.map((source) => (
                    <li key={source.name}>
                      <strong>{source.url ? <a href={source.url} rel="noreferrer" target="_blank">{source.name}</a> : source.name}</strong>
                      : {source.use}
                    </li>
                  ))}
                </ul>
              </Section>

              <Section id="limits">
                <ul className={styles.points}>
                  <li>
                    <strong>Bridged supplies.</strong> Some tokens exist on many chains at once. xStocks&apos; venue total
                    uses the issuer&apos;s circulating supply, counted once, but its per-chain supplies include bridged
                    copies and can add up to more than that, so its split by chain is approximate.
                  </li>
                  <li>
                    <strong>Chains not read.</strong> Some issuers deploy on chains Panther does not read. Those deployments
                    are not counted.
                  </li>
                  <li>
                    <strong>Different daily windows.</strong> Robinhood&apos;s trading volume and mint + burn are
                    Robinhood&apos;s &ldquo;today&rdquo;; other venues&apos; mint + burn is a rolling 24 hours.
                  </li>
                  <li>
                    <strong>Bridges count as flow.</strong> A bridge transfer that burns on one chain and mints on another
                    counts on both chains.
                  </li>
                  <li>
                    <strong>Discovery.</strong> Venues found from CoinGecko listings or short hand-checked lists can miss a
                    new token until it is listed or added.
                  </li>
                  <li>
                    <strong>Prices.</strong> Some tokens have no usable price at a given moment and have no market cap until
                    one arrives.
                  </li>
                </ul>
              </Section>

              <Section id="api">
                <p>
                  Every figure on the terminal comes from the public API at{" "}
                  <a href={apiV1} rel="noreferrer" target="_blank">{apiV1.replace(/^https?:\/\//, "")}</a>. It is edge-cached
                  and open to anyone.
                </p>
                <ul className={styles.points}>
                  <li><code>/v1/venues</code>: the venue list.</li>
                  <li>
                    <code>/v1/venues/{"{venue}"}/board</code>: one venue&apos;s board (<code>panther.board.v1</code>): every
                    token with its price, price basis, supply, per-chain deployments, market cap, flows and 24h
                    changes, plus the venue totals. Missing values are <code>null</code>.
                  </li>
                  <li><code>/v1/status</code>: when each collection source last ran and last succeeded.</li>
                </ul>
              </Section>
            </div>
          </div>
        </div>
      </main>
      <footer className="footer">
        Panther Research · Independent market data. Not affiliated with Robinhood or any issuer. Not investment advice.
      </footer>
    </div>
  );
}
